import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/app/dashboard';

  const [email, setEmail] = useState('admin@aste.local');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!loading && user) return <Navigate to={from} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password, remember);
      navigate(from, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.email?.[0] ||
        err?.response?.data?.message ||
        'Errore durante il login';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lg">
      <aside className="lg__brand" aria-hidden="true">
        <div className="lg__brand-top">
          <div className="lg__mark">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2 4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6l-8-4z" />
            </svg>
          </div>
          <div>
            <div className="lg__brand-name">Rivelasta</div>
            <div className="lg__brand-sub">Piattaforma per aste giudiziarie</div>
          </div>
        </div>

        <div className="lg__brand-mid">
          <div className="lg__eyebrow"><i></i> Studio Legale · accesso riservato</div>
          <h1 className="lg__headline">
            Trasforma le aste giudiziarie<br />in <em>opportunità misurabili</em>.
          </h1>
          <p className="lg__lede">
            Monitora i lotti, abbina automaticamente lead e immobili,
            confronta valutazioni e gestisci le trattative in un unico posto.
          </p>

          <div className="lg__metrics">
            <div className="lg__metric">
              <div className="lg__metric-num">12</div>
              <div className="lg__metric-lbl">Match oggi</div>
            </div>
            <div className="lg__metric">
              <div className="lg__metric-num">128</div>
              <div className="lg__metric-lbl">Immobili attivi</div>
            </div>
            <div className="lg__metric">
              <div className="lg__metric-num">34</div>
              <div className="lg__metric-lbl">Lead in portfolio</div>
            </div>
          </div>
        </div>

        <div className="lg__brand-foot">
          <span>© 2026 Rivelasta</span>
          <span>
            <a href="#">Termini</a>
            <a href="#">Privacy</a>
            <a href="#">Sicurezza</a>
          </span>
        </div>
      </aside>

      <section className="lg__form-wrap">
        <div className="lg__top-right">
          <span className="lg__shield">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
            Connessione cifrata
          </span>
          <span>Nuovo qui? <Link to="/">Torna alla landing</Link></span>
        </div>

        <form className="lg__form" onSubmit={handleSubmit}>
          <div className="lg__welcome-eyebrow">Bentornato</div>
          <h2 className="lg__title">Accedi al tuo workspace</h2>
          <p className="lg__sub">Usa le tue credenziali per continuare.</p>

          {error && (
            <div
              role="alert"
              style={{
                background: 'oklch(0.96 0.04 25)',
                border: '1px solid oklch(0.75 0.12 25)',
                color: 'oklch(0.35 0.15 25)',
                padding: '10px 12px',
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          <div className="lg__field">
            <label className="lg__lbl" htmlFor="lg-email">Email</label>
            <div className="lg__input-wrap">
              <span className="lg__input-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </span>
              <input
                id="lg-email"
                className="lg__input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="lg__field">
            <div className="lg__lbl-row">
              <label className="lg__lbl" htmlFor="lg-pwd">Password</label>
              <a className="lg__lbl-link" href="#">Password dimenticata?</a>
            </div>
            <div className="lg__input-wrap">
              <span className="lg__input-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <input
                id="lg-pwd"
                className="lg__input"
                type={showPwd ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="lg__pass-toggle"
                aria-label={showPwd ? 'Nascondi password' : 'Mostra password'}
                onClick={() => setShowPwd((s) => !s)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>
          </div>

          <div className="lg__row">
            <label className="lg__check">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span className="lg__check-box"></span>
              <span>Resta connesso 14 giorni</span>
            </label>
          </div>

          <button type="submit" className="lg__submit" disabled={submitting}>
            <span>{submitting ? 'Accesso…' : 'Accedi'}</span>
            {!submitting && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            )}
          </button>

          <div className="lg__form-foot">
            Per supporto contatta <a href="mailto:it@studio-rivelasta.it">it@studio-rivelasta.it</a>
          </div>
        </form>
      </section>
    </div>
  );
}
